<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import OverflowMenu from '@/components/shared/OverflowMenu.vue'
import GalleryPicture from '@/components/organizer/galleries/GalleryPicture.vue'
import { UploadStatus } from '@/composables/useGalleryUploadManager'
import type { UploadEntry } from '@/composables/useGalleryUploadManager'
import { ExternalVideoProvider, GalleryItemStatus, GalleryItemType } from '@/types/galleries'
import type { OrganizerGalleryItem } from '@/types/galleries'
import { itemDisplayName, itemPreviewImage } from '@/utils/galleries/imageSources'
import { failureForItemErrorCode } from '@/utils/galleries/uploadFailure'

const props = defineProps<{
  item?: OrganizerGalleryItem
  upload?: UploadEntry
  selected: boolean
  selectionMode: boolean
  position: number
  total: number
  isCover?: boolean
  /** Local preview of a file uploaded in this session, shown while the server processes it. */
  previewUrl?: string | null
  reorderEnabled?: boolean
  dragging?: boolean
  dragOver?: boolean
  lifted?: boolean
}>()

const emit = defineEmits<{
  'toggle-select': [event: MouseEvent | KeyboardEvent]
  open: []
  retry: []
  remove: []
  cancel: []
  'hide-toggle': []
  'set-cover': []
  'move-to': []
  'move-to-start': []
  'move-to-end': []
  delete: []
  'drag-start': [event: DragEvent]
  'drag-over': [event: DragEvent]
  drop: [event: DragEvent]
  'drag-end': []
  'handle-keydown': [event: KeyboardEvent]
}>()

const { t } = useI18n()

type TileState = 'queued' | 'preparing' | 'uploading' | 'processing' | 'ready' | 'failed'

const state = computed<TileState>(() => {
  if (props.upload !== undefined) {
    switch (props.upload.status) {
      case UploadStatus.Preparing:
        return 'preparing'
      case UploadStatus.Reserving:
      case UploadStatus.Uploading:
      case UploadStatus.Completing:
        return 'uploading'
      case UploadStatus.Processing:
        return 'processing'
      case UploadStatus.Failed:
        return 'failed'
      default:
        return 'queued'
    }
  }
  if (props.item === undefined) return 'queued'
  if (props.item.status === GalleryItemStatus.Failed) return 'failed'
  if (props.item.status === GalleryItemStatus.Processing) return 'processing'
  return 'ready'
})

const label = computed(() => {
  if (props.upload !== undefined) return props.upload.fileName
  return props.item === undefined ? '' : itemDisplayName(props.item)
})

const image = computed(() => (props.item === undefined ? null : itemPreviewImage(props.item)))

const localPreview = computed(() => props.upload?.previewUrl ?? props.previewUrl ?? null)

const failureReason = computed<string | null>(() => {
  if (state.value !== 'failed') return null
  if (props.upload?.failure) return props.upload.failure.reason
  if (props.item) return failureForItemErrorCode(props.item.error_code).reason
  return null
})

const canRetry = computed(() => {
  if (state.value !== 'failed') return false
  if (props.upload?.failure) return props.upload.failure.retryable
  if (props.item) return failureForItemErrorCode(props.item.error_code).retryable
  return false
})

const isImage = computed(() => props.item?.type === GalleryItemType.Image)
const missingAlt = computed(
  () => state.value === 'ready' && isImage.value && (props.item?.alt_text ?? '').trim() === '',
)
const providerLabel = computed(() => {
  const provider = props.item?.external_video?.provider
  if (provider === ExternalVideoProvider.YouTube) return t('galleries.tile.youtube')
  if (provider === ExternalVideoProvider.Vimeo) return t('galleries.tile.vimeo')
  return null
})
const duration = computed(() => {
  const seconds = props.item?.video?.duration_seconds
  if (seconds === null || seconds === undefined) return null
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
})
const isVideoLike = computed(() => props.item !== undefined && props.item.type !== GalleryItemType.Image)

const progress = computed(() => Math.round(props.upload?.progress ?? 0))
const statusText = computed(() => {
  switch (state.value) {
    case 'queued':
      return t('galleries.tile.waiting')
    case 'preparing':
      return props.upload?.kind === 'video' ? t('galleries.tile.preparing_video') : t('galleries.tile.preparing')
    case 'uploading':
      return t('galleries.tile.uploading', { percent: progress.value })
    case 'processing':
      return t('galleries.tile.processing')
    default:
      return ''
  }
})

function onTileClick(event: MouseEvent): void {
  if (props.item === undefined) return
  if (props.selectionMode || event.shiftKey || event.ctrlKey || event.metaKey) {
    emit('toggle-select', event)
    return
  }
  emit('open')
}
</script>

<template>
  <li
    class="group relative aspect-square rounded-xl overflow-hidden bg-slate-800 border transition-colors"
    :class="[
      selected ? 'border-emerald-500 ring-2 ring-emerald-500' : state === 'failed' ? 'border-red-500/60' : 'border-slate-700',
      dragOver ? 'ring-2 ring-emerald-400' : '',
      dragging ? 'opacity-40' : '',
      lifted ? 'ring-2 ring-emerald-400' : '',
    ]"
    :data-testid="item ? `gallery-tile-${item.id}` : `gallery-tile-upload-${upload?.id}`"
    :data-state="state"
    @dragover="emit('drag-over', $event)"
    @drop="emit('drop', $event)"
  >
    <!-- Media -->
    <button
      v-if="item !== undefined"
      type="button"
      class="absolute inset-0 w-full h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-inset"
      :class="state === 'processing' ? 'opacity-60' : ''"
      :aria-label="label || t('galleries.tile.open')"
      :aria-pressed="selectionMode ? selected : undefined"
      @click="onTileClick"
    >
      <GalleryPicture
        v-if="image !== null && state !== 'failed'"
        :image="image"
        :alt="item.alt_text ?? ''"
      />
      <img
        v-else-if="localPreview"
        :src="localPreview"
        alt=""
        class="w-full h-full object-cover"
        draggable="false"
      />
      <span v-else class="flex w-full h-full items-center justify-center text-slate-600">
        <svg class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21zm10.5-11.25h.008v.008h-.008V9.75z" />
        </svg>
      </span>
    </button>
    <div v-else class="absolute inset-0">
      <img v-if="localPreview" :src="localPreview" alt="" class="w-full h-full object-cover opacity-50" draggable="false" />
    </div>

    <!-- Video play badge -->
    <span
      v-if="isVideoLike && state === 'ready'"
      class="pointer-events-none absolute inset-0 flex items-center justify-center"
      aria-hidden="true"
    >
      <span class="w-10 h-10 rounded-full bg-black/60 flex items-center justify-center text-white">
        <svg class="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
      </span>
    </span>

    <!-- State overlays -->
    <div
      v-if="state === 'queued' || state === 'preparing' || state === 'uploading' || state === 'processing'"
      class="absolute inset-x-0 bottom-0 bg-black/70 px-2 py-1.5"
      role="status"
    >
      <div class="flex items-center gap-2 text-xs text-slate-200">
        <AppSpinner v-if="state !== 'queued'" size="sm" />
        <span class="truncate">{{ statusText }}</span>
        <button
          v-if="upload !== undefined && state !== 'processing'"
          type="button"
          class="ml-auto text-slate-300 hover:text-white"
          :aria-label="t('galleries.tile.cancel')"
          @click.stop="emit('cancel')"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div v-if="state === 'uploading'" class="mt-1 h-1 rounded-full bg-slate-600 overflow-hidden">
        <div class="h-full bg-emerald-500 transition-all" :style="{ width: `${progress}%` }" />
      </div>
    </div>

    <div
      v-if="state === 'failed'"
      class="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-slate-900/85 px-3 text-center"
      role="alert"
    >
      <p class="text-xs text-red-300">
        {{ failureReason ? t(`galleries.reasons.${failureReason}`, { max: 50 }) : t('galleries.reasons.upload_failed') }}
      </p>
      <p class="text-xs text-slate-400 truncate max-w-full">{{ label }}</p>
      <div class="flex items-center gap-2">
        <button
          v-if="canRetry"
          type="button"
          class="bg-slate-700 hover:bg-slate-600 text-white rounded-md px-2.5 py-1 text-xs font-medium"
          @click.stop="emit('retry')"
        >
          {{ t('galleries.tile.retry') }}
        </button>
        <button
          type="button"
          class="border border-slate-600 hover:border-slate-500 text-slate-200 rounded-md px-2.5 py-1 text-xs font-medium"
          @click.stop="item !== undefined ? emit('delete') : emit('remove')"
        >
          {{ item !== undefined ? t('galleries.tile.delete') : t('galleries.tile.remove') }}
        </button>
      </div>
    </div>

    <!-- Badges -->
    <div v-if="state === 'ready'" class="pointer-events-none absolute left-2 bottom-2 flex flex-wrap gap-1">
      <span v-if="isCover" class="rounded bg-emerald-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
        {{ t('galleries.tile.cover') }}
      </span>
      <span v-if="providerLabel" class="rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-semibold text-white">{{ providerLabel }}</span>
      <span v-if="duration" class="rounded bg-black/70 px-1.5 py-0.5 text-[10px] font-semibold text-white">{{ duration }}</span>
      <span v-if="missingAlt" class="rounded bg-amber-500 px-1.5 py-0.5 text-[10px] font-semibold text-slate-900" data-testid="no-alt-chip">
        {{ t('galleries.tile.no_alt') }}
      </span>
    </div>
    <span
      v-if="item?.is_hidden"
      class="pointer-events-none absolute right-2 bottom-2 w-6 h-6 rounded-full bg-black/70 flex items-center justify-center text-slate-200"
      :title="t('galleries.tile.hidden')"
      data-testid="hidden-badge"
    >
      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
      </svg>
    </span>

    <!-- Controls (items only) -->
    <template v-if="item !== undefined">
      <button
        type="button"
        class="absolute top-2 left-2 w-6 h-6 rounded-md border flex items-center justify-center transition-opacity focus:opacity-100"
        :class="[
          selected ? 'bg-emerald-500 border-emerald-500 text-white opacity-100' : 'bg-black/50 border-white/60 text-transparent',
          selectionMode || selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
        ]"
        :aria-label="t('galleries.tile.select', { name: label })"
        :aria-pressed="selected"
        @click.stop="emit('toggle-select', $event)"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M4.5 12.75l6 6 9-13.5" />
        </svg>
      </button>

      <button
        v-if="reorderEnabled"
        type="button"
        draggable="true"
        class="absolute top-2 left-10 w-6 h-6 rounded-md bg-black/50 text-white flex items-center justify-center cursor-grab opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
        :class="lifted ? 'opacity-100' : ''"
        :aria-label="t('galleries.tile.drag', { name: label, position, total })"
        :aria-pressed="lifted"
        :data-reorder-handle="item.id"
        @dragstart="emit('drag-start', $event)"
        @dragend="emit('drag-end')"
        @keydown="emit('handle-keydown', $event)"
      >
        <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M8 6a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm8 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3zM8 13.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm8 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3zM8 21a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm8 0a1.5 1.5 0 110-3 1.5 1.5 0 010 3z" />
        </svg>
      </button>

      <div class="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
        <OverflowMenu :aria-label="t('galleries.tile.actions', { name: label })" menu-class="w-52" teleport>
          <button type="button" class="w-full text-left px-4 py-2 text-sm text-slate-200 hover:bg-slate-700/50" @click="emit('open')">
            {{ t('galleries.tile.edit') }}
          </button>
          <button
            v-if="state === 'ready' && !isCover"
            type="button"
            class="w-full text-left px-4 py-2 text-sm text-slate-200 hover:bg-slate-700/50"
            @click="emit('set-cover')"
          >
            {{ t('galleries.tile.set_cover') }}
          </button>
          <button type="button" class="w-full text-left px-4 py-2 text-sm text-slate-200 hover:bg-slate-700/50" @click="emit('hide-toggle')">
            {{ item.is_hidden ? t('galleries.tile.show') : t('galleries.tile.hide') }}
          </button>
          <button type="button" class="w-full text-left px-4 py-2 text-sm text-slate-200 hover:bg-slate-700/50" @click="emit('move-to')">
            {{ t('galleries.tile.move_to') }}
          </button>
          <button
            type="button"
            class="w-full text-left px-4 py-2 text-sm text-slate-200 hover:bg-slate-700/50 disabled:opacity-40"
            :disabled="position === 1"
            @click="emit('move-to-start')"
          >
            {{ t('galleries.tile.move_start') }}
          </button>
          <button
            type="button"
            class="w-full text-left px-4 py-2 text-sm text-slate-200 hover:bg-slate-700/50 disabled:opacity-40"
            :disabled="position === total"
            @click="emit('move-to-end')"
          >
            {{ t('galleries.tile.move_end') }}
          </button>
          <button type="button" class="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-slate-700/50" @click="emit('delete')">
            {{ t('galleries.tile.delete') }}
          </button>
        </OverflowMenu>
      </div>
    </template>
  </li>
</template>
